// The polar lift, in WGSL. The eye branch's only lift.

import positionMapShader from './wgsl/eye_position_map.wgsl?raw'
import liftShader from './wgsl/eye_lift.wgsl?raw'
import {
  LIFT_PARAMS_BYTES, POSITION_MAP_PARAMS_BYTES, packEyeLiftParams, packPositionMapParams,
  packTexelStatics,
} from './eyeLiftParams.ts'
import { EMPTY_LIGHT_SET } from './eyeSceneLights.ts'
import type { EyeLightSet } from './eyeSceneLights.ts'
import type { EyeChart, EyeMeta } from './eyeChart.ts'
import type { EyeDecoderMaps } from './eyeDecoders.ts'
import type { EyePose } from './controller/index.ts'
import { EYE_SIDES } from './controller/index.ts'
import type { EyeSide } from './eyeSide.ts'
import type { Vector3 } from '../math/index.ts'

/** centre 3 + covariance 6 + colour 1 — see eye_lift.wgsl's write_three_splat. */
export const WORDS_PER_SLOT = 10

type EyeResources = {
  chart: EyeChart
  numTexels: number
  outputOffset: number
  vertexBytes: number
  verticesBuffer: GPUBuffer
  triangleIndicesBuffer: GPUBuffer
  barycentricWeightsBuffer: GPUBuffer
  texelStaticsBuffer: GPUBuffer
  positionMapBuffer: GPUBuffer
  geomUvBuffer: GPUBuffer
  colorUvBuffer: GPUBuffer
  positionMapParamsBuffer: GPUBuffer
  liftParamsBuffer: GPUBuffer
  positionMapBindGroup: GPUBindGroup
  liftBindGroup: GPUBindGroup
}

/** One eye's input for a pass: where the controller put it, and what the decoders said. */
export type EyeLiftPass = {
  pose: EyePose
  maps: EyeDecoderMaps
}

export class GpuEyeLifter {
  private device: GPUDevice
  private meta: EyeMeta
  private eyes: Record<EyeSide, EyeResources>
  private positionMapPipeline: GPUComputePipeline
  private liftPipeline: GPUComputePipeline
  private outputBuffer: GPUBuffer
  private readbackBuffer: GPUBuffer
  /** Words per slot across the three blocks the lift writes (centre 3, covariance 6, colour 1). */
  readonly capacity: number
  readonly count: number
  private specGain = 1
  /** The live scene's lights; empty until the mount supplies them, so no glint beats a wrong one. */
  private lights: EyeLightSet = EMPTY_LIGHT_SET
  private destroyed = false
  private inFlight = false
  /** Wall-clock of the last liftBoth(), split so the readback is visible on its own. */
  lastSubmitMs = -1
  lastReadbackMs = -1

  constructor(device: GPUDevice, charts: Record<EyeSide, EyeChart>, meta: EyeMeta,
              vertexCounts: Record<EyeSide, number>, capacity: number) {
    this.device = device
    this.meta = meta
    this.capacity = capacity
    this.count = charts.left.numTexels + charts.right.numTexels
    if (capacity < this.count) {
      throw new Error(`eye gpu lift: capacity ${capacity} < ${this.count} splats`)
    }

    const pipeline = (code: string, label: string) => device.createComputePipeline({
      label, layout: 'auto',
      compute: { module: device.createShaderModule({ code, label }), entryPoint: 'main' },
    })
    this.positionMapPipeline = pipeline(positionMapShader, 'eye_position_map')
    this.liftPipeline = pipeline(liftShader, 'eye_lift')

    // Both word blocks in one buffer, so the readback is two contiguous copies.
    this.outputBuffer = device.createBuffer({
      label: 'eye_ext_words',
      size: capacity * WORDS_PER_SLOT * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    })
    this.readbackBuffer = device.createBuffer({
      label: 'eye_readback',
      size: capacity * WORDS_PER_SLOT * 4,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    })

    let outputOffset = 0
    this.eyes = {} as Record<EyeSide, EyeResources>
    for (const side of EYE_SIDES) {
      this.eyes[side] = this.buildEye(side, charts[side], vertexCounts[side], outputOffset)
      outputOffset += charts[side].numTexels
    }
  }

  private storage(data: Float32Array | Uint32Array, label: string): GPUBuffer {
    const usage = GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    const buffer = this.device.createBuffer({ label, size: data.byteLength, usage })
    this.device.queue.writeBuffer(buffer, 0, data.buffer as ArrayBuffer, data.byteOffset, data.byteLength)
    return buffer
  }

  private buildEye(side: EyeSide, chart: EyeChart, vertexCount: number,
                   outputOffset: number): EyeResources {
    const device = this.device
    const mapTexels = chart.resolution * chart.resolution
    const STORAGE = GPUBufferUsage.STORAGE
    const COPY_DST = GPUBufferUsage.COPY_DST
    const UNIFORM = GPUBufferUsage.UNIFORM

    const resources: EyeResources = {
      chart,
      numTexels: chart.numTexels,
      outputOffset,
      vertexBytes: vertexCount * 3 * 4,
      // Written every pass from the controller's posed vertices; ~7 kB beside 0.9 MB of maps.
      verticesBuffer: device.createBuffer({ label: `eye_${side}_vertices`, size: vertexCount * 3 * 4, usage: STORAGE | COPY_DST }),
      triangleIndicesBuffer: this.storage(chart.triangleIndices, `eye_${side}_idxim`),
      barycentricWeightsBuffer: this.storage(chart.barycentricWeights, `eye_${side}_barim`),
      texelStaticsBuffer: this.storage(packTexelStatics(chart), `eye_${side}_texel`),
      positionMapBuffer: device.createBuffer({ label: `eye_${side}_position_map`, size: mapTexels * 3 * 4, usage: STORAGE }),
      geomUvBuffer: device.createBuffer({ label: `eye_${side}_geom_uv`, size: 11 * mapTexels * 4, usage: STORAGE | COPY_DST }),
      colorUvBuffer: device.createBuffer({ label: `eye_${side}_color_uv`, size: 4 * mapTexels * 4, usage: STORAGE | COPY_DST }),
      positionMapParamsBuffer: device.createBuffer({ label: `eye_${side}_position_map_params`, size: POSITION_MAP_PARAMS_BYTES, usage: UNIFORM | COPY_DST }),
      liftParamsBuffer: device.createBuffer({ label: `eye_${side}_lift_params`, size: LIFT_PARAMS_BYTES, usage: UNIFORM | COPY_DST }),
      positionMapBindGroup: null as unknown as GPUBindGroup,
      liftBindGroup: null as unknown as GPUBindGroup,
    }
    resources.positionMapBindGroup = device.createBindGroup({
      label: `eye_${side}_position_map_bind`,
      layout: this.positionMapPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: resources.verticesBuffer } },
        { binding: 1, resource: { buffer: resources.triangleIndicesBuffer } },
        { binding: 2, resource: { buffer: resources.barycentricWeightsBuffer } },
        { binding: 3, resource: { buffer: resources.positionMapBuffer } },
        { binding: 4, resource: { buffer: resources.positionMapParamsBuffer } },
      ],
    })
    resources.liftBindGroup = device.createBindGroup({
      label: `eye_${side}_lift_bind`,
      layout: this.liftPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: resources.texelStaticsBuffer } },
        { binding: 1, resource: { buffer: resources.positionMapBuffer } },
        { binding: 2, resource: { buffer: resources.geomUvBuffer } },
        { binding: 3, resource: { buffer: resources.colorUvBuffer } },
        { binding: 4, resource: { buffer: this.outputBuffer } },
        { binding: 5, resource: { buffer: resources.liftParamsBuffer } },
      ],
    })
    // The position-map params never change: the shader's only scalar is the map size.
    device.queue.writeBuffer(resources.positionMapParamsBuffer, 0, packPositionMapParams(mapTexels))
    return resources
  }

  setSpecGain(gain: number): void { this.specGain = gain }
  setLights(lights: EyeLightSet): void { this.lights = lights }

  /** Both eyes, one submit, one readback. `out` is filled with the ExtSplats A words then the
   *  B words — capacity*4 each — ready for two `.set()` calls. */
  async liftBoth(passes: Record<EyeSide, EyeLiftPass>, cameraCentre: Vector3 | null,
                 out: Uint32Array): Promise<Uint32Array> {
    if (this.destroyed) throw new Error('eye gpu lift: used after dispose()')
    // One readback buffer, so a concurrent pass would mapAsync an already-mapped buffer.
    if (this.inFlight) throw new Error('eye gpu lift: liftBoth() is already in flight')
    if (out.length < this.capacity * WORDS_PER_SLOT) {
      throw new Error(`eye gpu lift: out needs ${this.capacity * WORDS_PER_SLOT} words, got ${out.length}`)
    }
    this.inFlight = true
    try {
      return await this.encodeAndRead(passes, cameraCentre, out)
    } finally {
      this.inFlight = false
    }
  }

  private async encodeAndRead(passes: Record<EyeSide, EyeLiftPass>, cameraCentre: Vector3 | null,
                              out: Uint32Array): Promise<Uint32Array> {
    const submitStart = performance.now()
    const device = this.device
    const encoder = device.createCommandEncoder({ label: 'eye_lift' })
    for (const side of EYE_SIDES) {
      const resources = this.eyes[side]
      const { pose, maps } = passes[side]
      this.uploadPass(side, resources, pose, maps, cameraCentre)

      const pass = encoder.beginComputePass({ label: `eye_${side}` })
      pass.setPipeline(this.positionMapPipeline)
      pass.setBindGroup(0, resources.positionMapBindGroup)
      pass.dispatchWorkgroups(Math.ceil((resources.chart.resolution * resources.chart.resolution) / 64))
      pass.setPipeline(this.liftPipeline)
      pass.setBindGroup(0, resources.liftBindGroup)
      pass.dispatchWorkgroups(Math.ceil(resources.numTexels / 64))
      pass.end()
    }
    encoder.copyBufferToBuffer(this.outputBuffer, 0, this.readbackBuffer, 0, this.capacity * WORDS_PER_SLOT * 4)
    device.queue.submit([encoder.finish()])
    this.lastSubmitMs = performance.now() - submitStart

    const readbackStart = performance.now()
    await this.readbackBuffer.mapAsync(GPUMapMode.READ)
    out.set(new Uint32Array(this.readbackBuffer.getMappedRange()))
    this.readbackBuffer.unmap()
    this.lastReadbackMs = performance.now() - readbackStart
    return out
  }

  private uploadPass(side: EyeSide, resources: EyeResources, pose: EyePose,
                     maps: EyeDecoderMaps, cameraCentre: Vector3 | null): void {
    const mapTexels = resources.chart.resolution * resources.chart.resolution
    // The shader indexes maps by channel plane, so a short map reads the wrong channel.
    if (maps.geomUv.length !== 11 * mapTexels) {
      throw new Error(`eye gpu lift ${side}: geom_uv has ${maps.geomUv.length} values, `
        + `expected 11x${resources.chart.resolution}²`)
    }
    if (maps.colorUv.length !== maps.colorChannels * mapTexels) {
      throw new Error(`eye gpu lift ${side}: color_uv has ${maps.colorUv.length} values, `
        + `expected ${maps.colorChannels}x${resources.chart.resolution}²`)
    }
    if (pose.vertices.byteLength !== resources.vertexBytes) {
      throw new Error(`eye gpu lift ${side}: pose has ${pose.vertices.length / 3} vertices, `
        + `the chart was built for ${resources.vertexBytes / 12}`)
    }
    this.write(resources.verticesBuffer, pose.vertices)
    this.write(resources.geomUvBuffer, maps.geomUv)
    this.write(resources.colorUvBuffer, maps.colorUv)
    this.device.queue.writeBuffer(resources.liftParamsBuffer, 0, packEyeLiftParams({
      numTexels: resources.numTexels, resolution: resources.chart.resolution,
      colorChannels: maps.colorChannels, outOffset: resources.outputOffset,
      capacity: this.capacity,
      centre: pose.frame.centre, axis: pose.frame.axis, radius: pose.frame.radius,
      meridionalStep: resources.chart.meridionalStep,
      tangent: this.meta.tangent, normal: this.meta.normal,
      opacityBias: this.meta.opacityBias, specVisBias: this.meta.specVisBias,
      specGain: this.specGain, bsigGuard: this.meta.bsigGuard,
      sgNorm: this.meta.spec.sgNorm, cameraCentre, lights: this.lights,
    }))
  }

  private write(buffer: GPUBuffer, data: Float32Array): void {
    this.device.queue.writeBuffer(buffer, 0, data.buffer as ArrayBuffer, data.byteOffset, data.byteLength)
  }

  dispose(): void {
    if (this.destroyed) return
    this.destroyed = true
    for (const side of EYE_SIDES) {
      const resources = this.eyes[side]
      for (const buffer of [resources.verticesBuffer, resources.triangleIndicesBuffer,
        resources.barycentricWeightsBuffer, resources.texelStaticsBuffer,
        resources.positionMapBuffer, resources.geomUvBuffer, resources.colorUvBuffer,
        resources.positionMapParamsBuffer, resources.liftParamsBuffer]) buffer.destroy?.()
    }
    this.outputBuffer.destroy?.()
    this.readbackBuffer.destroy?.()
  }
}
